import { ROOT_NODE } from '@candulabs/craft';
import { theme } from 'canoe';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useEventing } from 'commons/eventing';
import { Flags, useFlags } from 'commons/hooks';
import { useCraftPropsContext } from 'craft/contexts';
import { useSelection } from 'craft/hooks';
import { setPaletteTab, useIsPalettePinned, usePaletteTab, useToolboxTab } from 'craft/state';

import { FullscreenTabs } from './FullscreenTabs';
import { Item } from './Item';
import { Panel } from './Panel';
import { PalettePlacement, TabItem, TabMode, tabs } from './tabs';

export const PALETTE_WIDTH = 62;

export const Container = styled.div`
  z-index: 5;
  top: 0;
  left: 0;
  position: sticky;
`;

const Spacer = styled.div`
  flex-grow: 1;
`;

export const Relative = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${PALETTE_WIDTH}px;
  background-color: ${theme.colors.OnyxGrayD10};
`;

export const Palette = React.memo(() => {
  const { mode: editorMode, documentId } = useCraftPropsContext();
  const palette = useRef<HTMLDivElement>(null);
  const timerLeave = useRef<NodeJS.Timeout | undefined>();
  const eventing = useEventing();
  const dispatch = useDispatch();
  const setTab = (tab?: string) => {
    if (tab) {
      eventing.track(`editor.clickPaletteTab.${tab}`);
    }
    dispatch(setPaletteTab(tab));
  };
  const isPinned = useIsPalettePinned();
  const tab = usePaletteTab();
  const { forcePlacementsTab } = useToolboxTab();
  const { getSerializedTree } = useSelection();

  const { [Flags.COLLECTIONS]: showCollections } = useFlags();

  useEffect(() => {
    if (forcePlacementsTab) {
      return;
    }
    const tree = getSerializedTree(ROOT_NODE);

    if (Object.keys(tree.nodes).length === 1) {
      setTab(topTabs[0].label);
    }
  }, []);

  const { fullscreenTabs, bottomTabs, topTabs } = useMemo(() => {
    const avaliableTabs = tabs.filter(({ label }) => {
      switch (label) {
        case 'Collections':
          return showCollections;
        default:
          return true;
      }
    });
    return {
      fullscreenTabs: tabs.filter(
        ({ tabMode, mode }) => mode.includes(editorMode) && tabMode === TabMode.FullScreen,
      ),
      bottomTabs: tabs.filter(
        ({ placement, mode }) => mode.includes(editorMode) && placement === PalettePlacement.Bottom,
      ),
      topTabs: avaliableTabs.filter(
        ({ placement, mode }) => mode.includes(editorMode) && placement === PalettePlacement.Top,
      ),
    };
  }, [tabs, editorMode]);

  // if it's a fullscreen tab, then don't close it
  const onMouseLeave = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (isPinned && activeTab?.label === 'Layers') ||
      fullscreenTabs.find(({ label }) => label === tab)
    ) {
      return;
    }
    timerLeave.current = setTimeout(() => {
      setTab();
      if (timerLeave.current) {
        clearTimeout(timerLeave.current);
      }
    }, 300);
  };
  const onMouseEnter = (e: React.MouseEvent) => {
    e.preventDefault();

    if (timerLeave.current) {
      clearTimeout(timerLeave.current);
    }
  };

  const selectTab = (tabItem?: TabItem | null) => {
    setTab(tabItem?.label);
  };

  let activeTab = tabs.find(({ label }) => label === tab);
  if (!activeTab && isPinned) {
    activeTab = tabs.find(({ label }) => label === 'Layers');
  }
  // We prefer to avoid arbitrary providers but have issues passing props through to tabs currently, this should be
  // visited in a future refactor of how we manage the Palette tabs
  return (
    <Container
      data-walkthrough="editor-palette"
      data-cy="palette"
      id="palette"
      ref={palette}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <FullscreenTabs documentId={documentId} />
      {/* Added the listener to avoid that the react-dnd tries to handle the onDragStart event  */}

      <Relative>
        {topTabs.map((tabItem) => (
          <Item
            active={tab === tabItem.label}
            key={tabItem.label}
            onClick={() => (tab === tabItem.label ? setTab() : selectTab(tabItem))}
            onFocus={() => selectTab(tabItem)}
            icon={tabItem.icon}
            label={tabItem.label.replace('All ', '')}
            data-cy={`palette-tab-item-${tabItem.label}`}
            data-palette-item={tabItem.label}
            disabled={forcePlacementsTab}
          />
        ))}
        <Spacer />
        {bottomTabs.map((tabItem) => (
          <Item
            active={tab === tabItem.label}
            key={tabItem.label}
            onClick={() => (tab === tabItem.label ? setTab() : selectTab(tabItem))}
            onFocus={() => selectTab(tabItem)}
            icon={tabItem.icon}
            label={tabItem.label}
            data-cy={`palette-tab-item-${tabItem.label}`}
            data-palette-item={tabItem.label}
            disabled={forcePlacementsTab}
          />
        ))}
        <Panel tab={activeTab} onClose={() => setTab()} />
      </Relative>
    </Container>
  );
});

Palette.displayName = 'Palette';
